import BooleanWithLabel from "@/Components/Boolean/BooleanWithLabel";
import Button from "@/Components/Button/Button";
import InputColor from "@/Components/Input/InputColor";
import Textfield from "@/Components/Input/Textfield";
import Modal, { ModalProps } from "@/Components/Modal/Modal";
import { useModalMeta, useSetModal } from "@/shared/atoms/useModal";
import useRestaurantTableMutation from "@/shared/mutations/restaurantMap/useRestaurantTableMutation";
import useRestaurantTableUpdate from "@/shared/mutations/restaurantMap/useRestaurantTableUpdate";
import useRestaurantMap from "@/shared/queries/restaurantMap/useRestaurantMap";
import { RestaurantTable } from "@/shared/utils/interfaces";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

function isOverlapping(
  x: number,
  y: number,
  width: number,
  height: number,
  tables: RestaurantTable[]
) {
  return tables.some((table) => {
    return !(
      (
        x + 100 <= parseInt(table.x) || // rechts van huidige
        x >= parseInt(table.x) + 100 || // links van huidige
        y + 100 <= parseInt(table.y) || // boven huidige
        y >= parseInt(table.y) + 100
      ) // onder huidige
    );
  });
}

function findFreeSpot(
  width: number,
  height: number,
  tables: RestaurantTable[],
  mapSize = 1000,
  step = 10
): { x: number; y: number } | null {
  for (let y = 0; y <= mapSize - height; y += step) {
    for (let x = 0; x <= mapSize - width; x += step) {
      if (!isOverlapping(x, y, width, height, tables)) {
        return { x, y };
      }
    }
  }
  return null; // geen vrije plek gevonden
}

export interface TableModalProps extends ModalProps {}

const TableModal = ({ onExit, ...props }: TableModalProps) => {
  const [advanced, setAdvanced] = useState(false);
  const [meta] = useModalMeta();
  const { table, mapId } = meta as { mapId: number; table: RestaurantTable };
  const create = useRestaurantTableMutation(mapId);
  const update = useRestaurantTableUpdate(mapId);
  const id = parseInt(window.location.pathname.split("/")[2]);
  const { data } = useRestaurantMap(id, true);
  const isUpdate = table !== undefined;
  const setModal = useSetModal();
  const { t } = useTranslation("", { keyPrefix: "tableModal" });
  const { t: tCommon } = useTranslation();
  const { handleSubmit, control, register, errors } = useForm({
    defaultValues: isUpdate ? table : undefined,
  });
  const req = isUpdate ? update.mutateAsync : create.mutateAsync;
  const onSubmit = (values: RestaurantTable) => {
    const position = findFreeSpot(100, 100, data.tables);

    const response = {
      x: position?.x ?? 100,
      y: position?.y ?? 100,
      width: 100,
      height: 100,
      ...table,
      ...values,
      number_of_seats_left: values.number_of_seats_left || undefined,
      number_of_seats_above: values.number_of_seats_above || undefined,
      number_of_seats_right: values.number_of_seats_right || undefined,
      number_of_seats_below: values.number_of_seats_below || undefined,
    };
    return req(response).then(() => {
      setModal("");
    });
  };

  return (
    <Modal width={"50rem"} onExit={onExit} {...props}>
      <div className="flex items-center">
        <h2>{t(isUpdate ? "updateTitle" : "title")}</h2>{" "}
        <div className="ml-[2rem]">
          <Controller
            name={`color`}
            defaultValue={"#8A6519"}
            control={control}
            rules={{
              required: {
                value: true,
                message: tCommon("fieldRequired"),
              },
            }}
            render={({ onChange, value, name }) => (
              <div className="relative">
                <InputColor
                  hideTextfield
                  name={name}
                  errors={errors}
                  label={tCommon("tableColor")}
                  onChange={(value) => {
                    onChange(value);
                  }}
                  value={value}
                />
              </div>
            )}
          />
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div style={{ display: !advanced ? "block" : "none" }}>
          <div className="mb-[2rem]">
            <Controller
              name={`is_active`}
              defaultValue={table ?? true}
              control={control}
              rules={{
                required: false,
              }}
              render={(props) => (
                <div className="mb-[2rem]">
                  <BooleanWithLabel
                    onChange={(e) => {
                      return props.onChange(e.target.checked);
                    }}
                    checked={props.value}
                  >
                    {tCommon("active")}
                  </BooleanWithLabel>
                </div>
              )}
            />
          </div>
          <div className="mb-[2rem]">
            <Textfield
              name="name"
              placeholder={t("placeholder")}
              error={errors?.name?.message}
              ref={register({
                required: {
                  value: true,
                  message: tCommon("fieldRequired"),
                },
              })}
              label={tCommon("name")}
            />
          </div>
          <div className="mb-[2rem]">
            <Textfield
              type="number"
              min="1"
              name="table_number"
              placeholder={`${tCommon("tableNumberPlaceholder")}`}
              error={errors?.name?.message}
              ref={register({
                valueAsNumber: true,
                required: {
                  value: true,
                  message: tCommon("fieldRequired"),
                },
              })}
              label={tCommon("tableNumber")}
            />
          </div>
          <div className="mb-[2rem]">
            <Textfield
              name="amount_of_seats"
              type="number"
              min="1"
              placeholder={tCommon("seatsPlaceholder")}
              error={errors?.name?.message}
              ref={register({
                valueAsNumber: true,
                required: {
                  value: true,
                  message: tCommon("fieldRequired"),
                },
              })}
              label={tCommon("seats")}
            />
          </div>
        </div>
        <div style={{ display: advanced ? "block" : "none" }}>
          <div className="mb-[2rem]">
            <Textfield
              name="number_of_seats_left"
              type="number"
              min="0"
              placeholder={tCommon("seatsPlaceholder")}
              error={errors?.number_of_seats_left?.message}
              ref={register({
                valueAsNumber: true,
                required: {
                  value: false,
                  message: tCommon("fieldRequired"),
                },
              })}
              label={tCommon("number_of_seats_left")}
            />
          </div>

          <div className="mb-[2rem]">
            <Textfield
              name="number_of_seats_above"
              type="number"
              min="0"
              placeholder={tCommon("seatsPlaceholder")}
              error={errors?.number_of_seats_above?.message}
              ref={register({
                valueAsNumber: true,
                required: {
                  value: false,
                  message: tCommon("fieldRequired"),
                },
              })}
              label={tCommon("number_of_seats_above")}
            />
          </div>

          <div className="mb-[2rem]">
            <Textfield
              name="number_of_seats_right"
              type="number"
              min="0"
              placeholder={tCommon("seatsPlaceholder")}
              error={errors?.number_of_seats_right?.message}
              ref={register({
                valueAsNumber: true,
                required: {
                  value: false,
                  message: tCommon("fieldRequired"),
                },
              })}
              label={tCommon("number_of_seats_right")}
            />
          </div>

          <div className="mb-[2rem]">
            <Textfield
              name="number_of_seats_below"
              type="number"
              min="0"
              placeholder={tCommon("seatsPlaceholder")}
              error={errors?.number_of_seats_below?.message}
              ref={register({
                valueAsNumber: true,
                required: {
                  value: false,
                  message: tCommon("fieldRequired"),
                },
              })}
              label={tCommon("number_of_seats_below")}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <Button type="submit">{tCommon("save")}</Button>
          <Button onClick={() => setAdvanced(!advanced)} type="button">
            {tCommon(advanced ? "settings" : "advancedSettings")}
          </Button>
        </div>

        {/* <Button variant="danger" onClick={() => remove(table.id)}>
            Verwijderen
          </Button> */}
      </form>
    </Modal>
  );
};

export default TableModal;
